import { useEffect } from 'react'
import { Alert, App, Form, Modal, Switch, TimePicker, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { upsertQuietHours, type DeliveryChannel, type QuietHoursView } from './notifyApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface QuietHoursFormModalProps {
  open: boolean
  channel: DeliveryChannel
  /** `null` = shu kanal uchun hozircha qator yo'q (cheklovsiz) */
  current: QuietHoursView | null
  onClose: () => void
}

interface FormValues {
  start: Dayjs
  end: Dayjs
  enabled: boolean
}

const TIME_FORMAT = 'HH:mm'

/**
 * Tinchlik oynasi — shu oraliqda xabar YUBORILMAYDI (tenant vaqt zonasi
 * bo'yicha, backend hisoblaydi).
 *
 * ⚠️ Boshlanish/tugash IKKITA ALOHIDA `TimePicker` — `TimePicker.RangePicker`
 * EMAS. Sabab: oyna YARIM TUNNI KESIB O'TISHI mumkin (22:00–08:00, backend
 * testlarida aynan shu holat sinalgan — `notify/README.md`), `RangePicker`
 * esa "tugash boshlanishdan oldin bo'lmasin" tartibini talab qiladi va bunday
 * qiymatni kiritishga yo'l bermaydi.
 *
 * ⚠️ Qator yo'q kanal = CHEKLOVSIZ (`QuietHoursRepository` javadoc'i, ham
 * `notifyApi.ts` da qayd etilgan) — shuning uchun "o'chirish" alohida amal
 * (`NotifyQuietHoursPage`), bu forma faqat yaratish/yangilashni qiladi.
 */
export function QuietHoursFormModal({
  open,
  channel,
  current,
  onClose,
}: QuietHoursFormModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      start: current ? dayjs(current.start, 'HH:mm:ss') : dayjs('22:00', TIME_FORMAT),
      end: current ? dayjs(current.end, 'HH:mm:ss') : dayjs('08:00', TIME_FORMAT),
      enabled: current?.enabled ?? true,
    })
  }, [open, current, form])

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      upsertQuietHours({
        channel,
        start: values.start.format('HH:mm:ss'),
        end: values.end.format('HH:mm:ss'),
        enabled: values.enabled,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.quietHours })
      // X-SIB-004 — muvaffaqiyatli amaldan keyin ANIQ tasdiq
      void message.success(t('common.toastSaved'))
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('notify.quietHours.editTitle', { channel: t(`notify.channelName.${channel}`) })}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={420}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Typography.Paragraph type="secondary">{t('notify.quietHours.formHint')}</Typography.Paragraph>

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="start"
          label={t('notify.quietHours.start')}
          rules={[{ required: true, message: t('notify.quietHours.rangeRequired') }]}
        >
          <TimePicker format={TIME_FORMAT} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="end"
          label={t('notify.quietHours.end')}
          extra={t('notify.quietHours.overnightHint')}
          rules={[{ required: true, message: t('notify.quietHours.rangeRequired') }]}
        >
          <TimePicker format={TIME_FORMAT} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="enabled" label={t('notify.quietHours.enabled')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
